import { ElementDto, SceneTypeDto } from "@repo/api/overlays";
import { Sequence, useVideoConfig } from "remotion";
import TextElement from "./TextElement.tsx";
import ImageElement from "./ImageElement.tsx";
import { AssetMediaAsyncDto } from "@repo/api/asset-media";
import VideoElement from "./VideoElement.tsx";
import RectangleElement from "./RectangleElement.tsx";
import { getHexWithOpacity, hexToRgba } from "../../utils/color.ts";
import { ElementTiming, msToFrames } from "../../utils/timing.ts";
import { useOpacity } from "../../utils/animation.ts";
import { useElementDraft } from "../../utils/element-draft.ts";
import { CSSProperties } from "react";

export type ElementProps = {
  element: ElementDto;
  elementTiming: ElementTiming | null;
  assetMedias: Record<string, AssetMediaAsyncDto>;
  sceneType: SceneTypeDto;
};

export function Element({ element, elementTiming, assetMedias }: ElementProps) {
  const { fps } = useVideoConfig();
  const draft = useElementDraft(element.externalId);

  const x = draft?.x ?? element.position.x;
  const y = draft?.y ?? element.position.y;

  let width: number | undefined;
  let height: number | undefined;

  if ("size" in element) {
    width = draft?.w ?? element.size.w;
    height = draft?.h ?? element.size.h;
  }

  let outlineStyle: CSSProperties["outlineStyle"] | undefined;
  let outlineWidth: number | undefined;
  let outlineColor: string | undefined;

  if ("border" in element && element.border !== null) {
    outlineStyle = "solid";
    outlineWidth = element.border.width;
    outlineColor = getHexWithOpacity(
      element.border.color.color,
      element.border.color.opacity ?? 100,
    );
  }

  let shadow: string | undefined;

  if ("shadow" in element && element.shadow !== null) {
    shadow = `drop-shadow(${element.shadow.x}px ${element.shadow.y}px ${element.shadow.blur}px ${hexToRgba(element.shadow.color.color, (element.shadow.color.opacity ?? element.shadow.opacity) / 100)})`;
  }

  const opacity = useOpacity({ element, elementTiming });

  if (!elementTiming) {
    return null;
  }

  if (element.isHidden) {
    return null;
  }

  return (
    <Sequence
      from={elementTiming.sceneStartAtFrame}
      durationInFrames={elementTiming.durationInFrames}
      style={{ pointerEvents: "none" }}
      premountFor={msToFrames(2000, fps)}
    >
      <div
        className="absolute cursor-default select-none touch-none"
        style={{
          top: 0,
          left: 0,
          width: width,
          height: height,
          transform: `translate3d(${x}px, ${y}px, 0)`,
        }}
      >
        <div
          className="h-full w-full overflow-hidden"
          style={{
            outlineStyle: outlineStyle,
            outlineWidth: outlineWidth,
            outlineColor: outlineColor,
            filter: shadow,
            borderRadius:
              "cornerRadius" in element ? element.cornerRadius : undefined,
            opacity: opacity / 100,
            overflow: "hidden",
          }}
        >
          {element.type === "TEXT" && (
            <TextElement
              element={element}
              fontSize={draft?.fontSize}
              text={draft?.text}
            />
          )}
          {element.type === "IMAGE" && (
            <ImageElement element={element} assetMedias={assetMedias} />
          )}
          {element.type === "VIDEO" && (
            <VideoElement element={element} assetMedias={assetMedias} />
          )}
          {element.type === "RECTANGLE" && (
            <RectangleElement element={element} />
          )}
        </div>
      </div>
    </Sequence>
  );
}
